import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import { FormField } from '../Shared/FormField';
import { SalutationSelect } from '../Shared/SalutationSelect';
import SearchSelect, { type SearchSelectOption } from '../Shared/SearchSelect';
import { useContactStore } from '../../store/useContactStore';
import { formatPersonName, toSalutation, type Salutation } from '../../utils/salutation';

export interface ContactPersonValue {
  salutation: Salutation | null;
  name: string;
}

/**
 * Where the current value came from — decides whether a context change (customer/object)
 * may replace it (FACF-102):
 * - `none`   nothing applied yet (or the user cleared the name) — auto-pick is armed
 * - `auto`   filled by auto-pick — replaced/cleared on the next context change
 * - `picked` chosen in the picker — survives object changes, cleared on customer change
 * - `typed`  entered manually — never touched by auto-pick, survives customer changes
 */
type ContactSource = 'none' | 'auto' | 'picked' | 'typed';

interface ContactPersonFieldsProps {
  /** Customer whose contacts are offered in the picker; empty = picker disabled. */
  customerId: string | null | undefined;
  salutation: Salutation | null | undefined;
  name: string;
  onChange: (value: ContactPersonValue) => void;
  /**
   * Contact preferred by the surrounding form (the object's responsible contact). Wins over
   * "first contact of the customer" when auto-picking.
   */
  preferredContactId?: string | null;
  /** Auto-fill from the preferred/first contact while nothing was typed or picked (default on). */
  autoPick?: boolean;
  nameLabel?: string;
  namePlaceholder?: string;
  nameRequired?: boolean;
  nameError?: string;
  /** why: ContactPersonName is MaxLength(200) on offers/invoices (ADR-0019, Api repo). */
  nameMaxLength?: number;
  disabled?: boolean;
}

interface ContactLike {
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

function toContactValue(contact: ContactLike, nameMaxLength: number): ContactPersonValue {
  return {
    salutation: toSalutation(contact.salutation),
    name: `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim().slice(0, nameMaxLength),
  };
}

/**
 * Decoupled contact person for offers/invoices (ADR-0019, Api repo): a picker copies
 * salutation + "First Last" from one of the customer's contacts, both stay editable
 * and are stored as plain values — no FK to the contact. With `autoPick` the object's
 * responsible contact (else the customer's first contact) is applied automatically as
 * long as the user has not typed or picked anything (FACF-102).
 */
export function ContactPersonFields({
  customerId,
  salutation,
  name,
  onChange,
  preferredContactId,
  autoPick = true,
  nameLabel,
  namePlaceholder,
  nameRequired,
  nameError,
  nameMaxLength = 200,
  disabled,
}: ContactPersonFieldsProps) {
  const { t } = useTranslation(['common']);
  const { contacts, loading, fetchContacts } = useContactStore();

  const [pickedId, setPickedId] = useState('');
  // why: the effects below must read the *current* source without re-running on every
  // change — the ref is the source of truth, the state mirror only drives the hint line.
  const sourceRef = useRef<ContactSource>('none');
  const [source, setSource] = useState<ContactSource>('none');
  const pickedIdRef = useRef('');
  // why: latest name/onChange for the effects without making them dependencies — the
  // resets must fire once per context change, not whenever the parent re-creates the
  // callback or the name changes.
  const nameRef = useRef(name);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    nameRef.current = name;
    onChangeRef.current = onChange;
  });
  const previousCustomerIdRef = useRef<string | null | undefined>(customerId);
  // why: the store keeps whatever response resolves last; remember which customer we
  // actually finished loading for so a stale response can never produce a false
  // "no contacts" state, and so the hint does not flash before the first fetch.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoadedFor(null);
    fetchContacts({ customerId, pageSize: 200 })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoadedFor(customerId);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, fetchContacts]);

  useEffect(() => {
    if (previousCustomerIdRef.current === customerId) return;
    previousCustomerIdRef.current = customerId;
    setPickedId('');
    pickedIdRef.current = '';
    // why: only values that came from a pick or auto-pick are cleared on customer change —
    // manually typed values survive a customer switch.
    if (sourceRef.current === 'picked' || sourceRef.current === 'auto') {
      sourceRef.current = 'none';
      setSource('none');
      onChangeRef.current({ salutation: null, name: '' });
    }
  }, [customerId]);

  // why: the store list is shared and may still hold another customer's page while
  // the new fetch is in flight — filter locally so the picker never shows foreign contacts.
  const customerContacts = useMemo(
    () => (customerId ? contacts.filter((c) => c.customerId === customerId) : []),
    [contacts, customerId],
  );

  // Auto-pick (FACF-102): the preferred (object's responsible) contact, else the customer's
  // first contact — only while nothing was typed or picked.
  useEffect(() => {
    if (!autoPick || !customerId || loadedFor !== customerId) return;
    const current = sourceRef.current;
    if (current === 'typed' || current === 'picked') return;

    const candidate =
      (preferredContactId
        ? customerContacts.find((c) => c.id === preferredContactId)
        : undefined) ?? customerContacts[0];

    if (!candidate?.id) {
      // why: the auto-filled value belonged to a context that no longer has a candidate.
      if (current === 'auto') {
        sourceRef.current = 'none';
        setSource('none');
        setPickedId('');
        pickedIdRef.current = '';
        onChangeRef.current({ salutation: null, name: '' });
      }
      return;
    }
    // why: pre-filled forms (edit slide-overs) carry a stored name with source 'none' —
    // never overwrite it.
    if (current === 'none' && nameRef.current !== '') return;
    if (current === 'auto' && pickedIdRef.current === candidate.id) return;

    sourceRef.current = 'auto';
    setSource('auto');
    setPickedId(candidate.id);
    pickedIdRef.current = candidate.id;
    onChangeRef.current(toContactValue(candidate, nameMaxLength));
  }, [autoPick, customerId, loadedFor, customerContacts, preferredContactId, nameMaxLength]);

  const options: SearchSelectOption[] = useMemo(
    () =>
      customerContacts
        .filter((c) => Boolean(c.id))
        .map((c) => ({
          id: c.id as string,
          label: formatPersonName(toSalutation(c.salutation), c.firstName, c.lastName, t),
          description: c.role ?? undefined,
        })),
    [customerContacts, t],
  );

  const handlePick = (id: string) => {
    setPickedId(id);
    pickedIdRef.current = id;
    if (!id) return;
    const contact = customerContacts.find((c) => c.id === id);
    if (!contact) return;
    sourceRef.current = 'picked';
    setSource('picked');
    onChange(toContactValue(contact, nameMaxLength));
  };

  const handleManualChange = (next: ContactPersonValue) => {
    // why: an emptied name re-arms the auto-pick for the next context change; anything
    // else typed is protected from being overwritten.
    const nextSource: ContactSource = next.name === '' ? 'none' : 'typed';
    sourceRef.current = nextSource;
    setSource(nextSource);
    setPickedId('');
    pickedIdRef.current = '';
    onChange(next);
  };

  const showNoContactsHint =
    Boolean(customerId) && loadedFor === customerId && !loading && customerContacts.length === 0;

  return (
    <div className="space-y-4">
      {showNoContactsHint ? (
        <div className="space-y-1">
          <span className="block text-[12px] font-medium text-slate-600">
            {t('common:contactPerson.pick')}
          </span>
          <p className="text-[12px] text-slate-500">{t('common:contactPerson.noContacts')}</p>
        </div>
      ) : (
        <SearchSelect
          label={t('common:contactPerson.pick')}
          options={options}
          value={pickedId}
          onChange={handlePick}
          placeholder={
            customerId
              ? t('common:contactPerson.pickPlaceholder')
              : t('common:contactPerson.selectCustomerFirst')
          }
          noResultsText={t('common:contactPerson.noResults')}
          disabled={disabled || !customerId}
        />
      )}
      <div className="space-y-1">
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-4">
          <SalutationSelect
            value={salutation}
            disabled={disabled}
            onChange={(value) => handleManualChange({ salutation: value, name })}
          />
          <FormField
            label={nameLabel ?? t('common:contactPerson.name')}
            required={nameRequired}
            value={name}
            onChange={(e) =>
              handleManualChange({ salutation: salutation ?? null, name: e.target.value })
            }
            placeholder={namePlaceholder}
            maxLength={nameMaxLength}
            error={nameError}
            disabled={disabled}
          />
        </div>
        {source === 'auto' && (
          <p className="text-[12px] text-slate-500">{t('common:contactPerson.autoPicked')}</p>
        )}
      </div>
    </div>
  );
}

ContactPersonFields.displayName = 'ContactPersonFields';

export default ContactPersonFields;
